import { useState, useEffect, useCallback, useMemo } from 'react';
import feedbackService from '../../../../services/feedback.service';
import {
  apiFetch,
  getApiBaseUrl,
  createApiHeaders,
} from '../../../../config/api';
import { debugError } from '../../../../utils/debugLog';

/**
 * useTemplateFeedback Hook
 * Manages feedback loading, version filtering, and sorting
 */
export const useTemplateFeedback = (open, template, selectedVersion) => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [versions, setVersions] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const loadVersions = useCallback(async () => {
    if (!template?.template_id) return;

    try {
      const headers = createApiHeaders();
      const allVersions = [];
      let page = 1;
      let hasMore = true;
      const pageSize = 100; // Use reasonable page size

      // Fetch all pages until we have all versions
      while (hasMore) {
        const params = new URLSearchParams({
          page: page.toString(),
          page_size: pageSize.toString(),
        });

        let response = await fetch(
          `${getApiBaseUrl()}/api/v1/templates/advanced/${template.template_id}/versions?${params.toString()}`,
          { headers }
        );

        if (!response.ok && response.status === 404) {
          response = await fetch(
            `${getApiBaseUrl()}/api/v1/templates/${template.template_id}/versions?${params.toString()}`,
            { headers }
          );
        }

        if (response.ok) {
          const data = await response.json();
          const versionsList = data.versions || data.templates || [];
          allVersions.push(...versionsList);

          // Check if there are more pages
          const total = data.total || 0;
          const currentPage = data.page || page;
          const totalPages = Math.ceil(total / pageSize);
          hasMore =
            currentPage < totalPages && versionsList.length === pageSize;

          page++;
        } else {
          hasMore = false;
        }
      }

      setVersions(allVersions);
    } catch (err) {
      debugError('Failed to load versions:', err);
    }
  }, [template?.template_id]);

  const loadExecutions = useCallback(async () => {
    if (!template?.template_id) return;

    try {
      const params = new URLSearchParams({
        template_id: template.template_id,
        page: '1',
        page_size: '500',
      });

      await apiFetch(`/api/v1/templates/executions/?${params.toString()}`);
    } catch (err) {
      debugError('Failed to load executions:', err);
    }
  }, [template?.template_id]);

  const loadFeedback = useCallback(async () => {
    if (!template?.template_id) return;

    setLoading(true);
    setError(null);

    try {
      let feedbackData;

      if (selectedVersion === 'all') {
        feedbackData = await feedbackService.getTemplateFeedback(
          template.template_id
        );
      } else {
        const versionNumber = parseInt(selectedVersion, 10);
        feedbackData = await apiFetch(
          `/api/v1/feedback/template/${template.template_id}/version/${versionNumber}`
        );
      }

      const feedbackList = Array.isArray(feedbackData)
        ? feedbackData
        : feedbackData.feedback || [];

      setFeedback(feedbackList);
      await loadExecutions();
    } catch (err) {
      debugError('Failed to load feedback:', err);
      setError(err.message || 'Failed to load feedback');
      setFeedback([]);
    } finally {
      setLoading(false);
    }
  }, [template?.template_id, selectedVersion, loadExecutions]);

  useEffect(() => {
    if (open && template?.template_id) {
      loadVersions();
    }
  }, [open, template?.template_id, loadVersions]);

  useEffect(() => {
    if (open && template?.template_id) {
      loadFeedback();
    }
  }, [open, template?.template_id, selectedVersion, loadFeedback]);

  const handleSort = useCallback(
    column => {
      if (sortBy === column) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(column);
        setSortOrder('desc');
      }
    },
    [sortBy, sortOrder]
  );

  const sortedFeedback = useMemo(() => {
    if (!feedback.length) return [];

    const sorted = [...feedback].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'rating':
          aValue = a.rating || 0;
          bValue = b.rating || 0;
          break;
        case 'created_at':
        default:
          aValue = new Date(a.created_at || 0);
          bValue = new Date(b.created_at || 0);
          break;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [feedback, sortBy, sortOrder]);

  return {
    feedback: sortedFeedback,
    loading,
    error,
    versions,
    sortBy,
    sortOrder,
    handleSort,
    loadFeedback,
    loadVersions,
  };
};
